import React, { useEffect, useState } from "react";
import { Alert, Tabs, Table, Button, Statistic, Input, Tag, message, Spin } from "antd";
import {
  SyncOutlined,
  DatabaseOutlined,
  WarningOutlined,
  RedoOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { MonitoringWrapper, StatsRow, StatCard, ToolbarRow, TableContainer } from "./styled";
import {
  getKoboSyncStatus,
  getKoboFailures,
  postKoboReconcile,
  getKoboHookFailures,
  postKoboHookRetry,
} from "../../api/monitoring";

const { Search } = Input;

function formatTime(isoString) {
  if (!isoString) return "-";
  const date = new Date(isoString);
  const now = new Date();
  const diffMs = now - date;
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  const diffDays = Math.floor(diffHr / 24);
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
}

const KoboSyncTab = () => {
  const { t } = useTranslation();
  const [syncStatus, setSyncStatus] = useState(null);
  const [failures, setFailures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reconciling, setReconciling] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [statusRes, failuresRes] = await Promise.all([
        getKoboSyncStatus(),
        getKoboFailures(50),
      ]);
      setSyncStatus(statusRes);
      setFailures(failuresRes?.failures || []);
    } catch (e) {
      message.error("Failed to load sync status");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleReconcile = async () => {
    setReconciling(true);
    try {
      const result = await postKoboReconcile(72);
      message.success(
        `${t("monitoring.reconcileSuccess")}: ${result?.recovered || 0} recovered`
      );
      fetchData();
    } catch (e) {
      message.error("Reconciliation failed");
    } finally {
      setReconciling(false);
    }
  };

  const failureColumns = [
    { title: "Kobo ID", dataIndex: "kobo_id", key: "kobo_id", width: 120 },
    { title: t("monitoring.hospital"), dataIndex: "hospital_name", key: "hospital_name" },
    {
      title: t("monitoring.error"),
      dataIndex: "error_message",
      key: "error_message",
      ellipsis: true,
    },
    {
      title: t("monitoring.time"),
      dataIndex: "created_at",
      key: "created_at",
      width: 120,
      render: (val) => formatTime(val),
    },
  ];

  if (loading) return <Spin size="large" />;

  return (
    <>
      {syncStatus?.kobo_error && (
        <Alert
          type="error"
          showIcon
          message={t("monitoring.koboUnavailable")}
          description={syncStatus.kobo_error}
          style={{ marginBottom: 16 }}
        />
      )}
      <StatsRow>
        <StatCard>
          <Statistic
            title={t("monitoring.localCount")}
            value={syncStatus?.local_count ?? "-"}
          />
        </StatCard>
        <StatCard>
          <Statistic
            title={t("monitoring.remoteCount")}
            value={syncStatus?.remote_count ?? "-"}
          />
        </StatCard>
        <StatCard>
          <Statistic
            title={t("monitoring.difference")}
            value={syncStatus?.difference ?? "-"}
            valueStyle={{
              color:
                syncStatus?.difference == null
                  ? undefined
                  : syncStatus.difference > 0
                    ? "#cf1322"
                    : "#3f8600",
            }}
          />
        </StatCard>
        <StatCard>
          <Statistic
            title={t("monitoring.failures24h")}
            value={syncStatus?.recent_failures_24h ?? 0}
            valueStyle={{
              color: syncStatus?.recent_failures_24h > 0 ? "#cf1322" : "#3f8600",
            }}
          />
        </StatCard>
      </StatsRow>

      <ToolbarRow>
        <Button
          type="primary"
          icon={<SyncOutlined />}
          loading={reconciling}
          onClick={handleReconcile}
        >
          {t("monitoring.reconcileNow")}
        </Button>
        <span style={{ color: "#888" }}>
          {t("monitoring.lastSuccess")}: {formatTime(syncStatus?.last_success)}
        </span>
      </ToolbarRow>

      {failures.length > 0 && (
        <TableContainer>
          <h4 style={{ marginBottom: 12 }}>{t("monitoring.recentFailures")}</h4>
          <Table
            dataSource={failures}
            columns={failureColumns}
            rowKey="id"
            size="small"
            pagination={{ defaultPageSize: 10, showSizeChanger: true, pageSizeOptions: ['10', '20', '50'] }}
          />
        </TableContainer>
      )}
    </>
  );
};

const KoboFormsTab = () => {
  const { t } = useTranslation();
  const [forms, setForms] = useState([]);
  const [koboError, setKoboError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  useEffect(() => {
    const fetchForms = async () => {
      try {
        const res = await getKoboSyncStatus();
        setForms(res?.forms || []);
        setKoboError(res?.kobo_error || null);
      } catch (e) {
        message.error("Failed to load forms");
      } finally {
        setLoading(false);
      }
    };
    fetchForms();
  }, []);

  const filtered = forms.filter((f) =>
    f.name?.toLowerCase().includes(search.toLowerCase())
  );

  const totalSubmissions = forms.reduce((sum, f) => sum + (f.submissions || 0), 0);
  const activeForms = forms.filter((f) => f.submissions > 0).length;

  const columns = [
    {
      title: t("monitoring.formName"),
      dataIndex: "name",
      key: "name",
      width: 250,
      ellipsis: true,
      sorter: (a, b) => (a.name || "").localeCompare(b.name || ""),
    },
    {
      title: "UID",
      dataIndex: "uid",
      key: "uid",
      width: 160,
      render: (val) => <code style={{ fontSize: 11 }}>{val}</code>,
    },
    {
      title: t("monitoring.submissions"),
      dataIndex: "submissions",
      key: "submissions",
      width: 110,
      sorter: (a, b) => a.submissions - b.submissions,
      defaultSortOrder: "descend",
      render: (val) => (
        <span style={{ fontWeight: val > 0 ? 600 : 400 }}>
          {val.toLocaleString()}
        </span>
      ),
    },
    {
      title: t("monitoring.status"),
      key: "status",
      width: 90,
      render: (_, record) =>
        record.submissions > 0 ? (
          <Tag color="green">{t("monitoring.active")}</Tag>
        ) : (
          <Tag>{t("monitoring.empty")}</Tag>
        ),
    },
    {
      title: t("monitoring.lastModified"),
      dataIndex: "date_modified",
      key: "date_modified",
      width: 150,
      render: (val) => formatTime(val),
    },
  ];

  return (
    <>
      {koboError && (
        <Alert
          type="error"
          showIcon
          message={t("monitoring.koboUnavailable")}
          description={koboError}
          style={{ marginBottom: 16 }}
        />
      )}
      <StatsRow>
        <StatCard>
          <Statistic title={t("monitoring.totalForms")} value={koboError ? "-" : forms.length} />
        </StatCard>
        <StatCard>
          <Statistic title={t("monitoring.activeForms")} value={koboError ? "-" : activeForms} />
        </StatCard>
        <StatCard>
          <Statistic
            title={t("monitoring.totalSubmissions")}
            value={koboError ? "-" : totalSubmissions.toLocaleString()}
          />
        </StatCard>
      </StatsRow>

      <ToolbarRow>
        <Search
          placeholder={t("monitoring.searchForm")}
          onChange={(e) => setSearch(e.target.value)}
          style={{ width: 300 }}
          allowClear
        />
      </ToolbarRow>

      <TableContainer>
        <Table
          dataSource={filtered}
          columns={columns}
          rowKey="uid"
          size="small"
          loading={loading}
          pagination={{ defaultPageSize: 20, showSizeChanger: true, pageSizeOptions: ['10', '20', '50', '100'] }}
        />
      </TableContainer>
    </>
  );
};

const KoboHookFailuresTab = () => {
  const { t } = useTranslation();
  const [failures, setFailures] = useState([]);
  const [loadError, setLoadError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [retrying, setRetrying] = useState(null);

  const fetchFailures = async (refresh = false) => {
    setLoading(true);
    try {
      const res = await getKoboHookFailures(100, refresh);
      // The axios wrapper resolves (not rejects) on HTTP errors, so check the shape
      if (Array.isArray(res?.failures)) {
        setFailures(res.failures);
        setLoadError(null);
      } else {
        setFailures([]);
        setLoadError(res?.data?.detail || t("monitoring.koboUnavailable"));
      }
    } catch (e) {
      message.error("Failed to load KoBo hook failures");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFailures();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleRetry = async (record) => {
    setRetrying(record.log_uid);
    try {
      const res = await postKoboHookRetry(
        record.form_uid,
        record.hook_uid,
        record.log_uid
      );
      if (res?.status === "successful") {
        message.success(t("monitoring.retrySuccess"));
        setFailures(failures.filter((f) => f.log_uid !== record.log_uid));
      } else {
        message.error(res?.detail || t("monitoring.retryFailed"));
      }
    } catch (e) {
      message.error(t("monitoring.retryFailed"));
    } finally {
      setRetrying(null);
    }
  };

  const handleRetryAll = async () => {
    if (!failures.length) return;
    setRetrying("all");
    const grouped = {};
    failures.forEach((f) => {
      const key = `${f.form_uid}__${f.hook_uid}`;
      if (!grouped[key]) grouped[key] = f;
    });

    let successCount = 0;
    for (const f of Object.values(grouped)) {
      try {
        const res = await postKoboHookRetry(f.form_uid, f.hook_uid, "");
        if (res?.status === "successful") successCount++;
      } catch (e) {}
    }
    message.success(`${t("monitoring.retrySuccess")}: ${successCount} hooks retried`);
    setRetrying(null);
    fetchFailures();
  };

  const columns = [
    {
      title: t("monitoring.formName"),
      dataIndex: "form_name",
      key: "form_name",
      width: 200,
      ellipsis: true,
    },
    {
      title: "Submission ID",
      dataIndex: "submission_id",
      key: "submission_id",
      width: 120,
    },
    {
      title: t("monitoring.status"),
      dataIndex: "status_code",
      key: "status_code",
      width: 80,
      render: (val) => <Tag color="red">{val}</Tag>,
    },
    {
      title: t("monitoring.error"),
      dataIndex: "message",
      key: "message",
      ellipsis: true,
      render: (val) => (
        <span style={{ fontSize: 12, color: "#666" }}>
          {val?.replace(/\s+/g, " ").trim() || "-"}
        </span>
      ),
    },
    {
      title: t("monitoring.time"),
      dataIndex: "date_modified",
      key: "date_modified",
      width: 120,
      render: (val) => formatTime(val),
    },
    {
      title: t("monitoring.retry") || "Retry",
      key: "action",
      width: 70,
      render: (_, record) => (
        <Button
          size="small"
          type="link"
          icon={<RedoOutlined />}
          loading={retrying === record.log_uid}
          onClick={() => handleRetry(record)}
        />
      ),
    },
  ];

  return (
    <>
      {loadError && (
        <Alert
          type="error"
          showIcon
          message={t("monitoring.koboUnavailable")}
          description={loadError}
          style={{ marginBottom: 16 }}
        />
      )}
      <StatsRow>
        <StatCard>
          <Statistic
            title={t("monitoring.hookFailures") || "Failed Deliveries"}
            value={loadError ? "-" : failures.length}
            valueStyle={{
              color: loadError ? undefined : failures.length > 0 ? "#cf1322" : "#3f8600",
            }}
          />
        </StatCard>
      </StatsRow>

      <ToolbarRow>
        <Button
          type="primary"
          danger
          icon={<RedoOutlined />}
          loading={retrying === "all"}
          onClick={handleRetryAll}
          disabled={failures.length === 0}
        >
          {t("monitoring.retryAll") || "Retry All"}
        </Button>
        <Button onClick={() => fetchFailures(true)}>{t("monitoring.refresh") || "Refresh"}</Button>
        <span style={{ marginLeft: "auto", color: "#888" }}>
          {t("monitoring.hookFailuresDesc") || "Submissions KoBo failed to deliver to our server"}
        </span>
      </ToolbarRow>

      <TableContainer>
        <Table
          dataSource={failures}
          columns={columns}
          rowKey="log_uid"
          size="small"
          loading={loading}
          pagination={{ defaultPageSize: 20, showSizeChanger: true, pageSizeOptions: ['10', '20', '50', '100'] }}
        />
      </TableContainer>
    </>
  );
};

const MonitoringPage = () => {
  const { t } = useTranslation();

  return (
    <MonitoringWrapper>
      <Tabs
        defaultActiveKey="1"
        size="large"
        items={[
          {
            key: "1",
            label: (
              <span>
                <SyncOutlined /> {t("monitoring.koboSync")}
              </span>
            ),
            children: <KoboSyncTab />,
          },
          {
            key: "2",
            label: (
              <span>
                <WarningOutlined /> {t("monitoring.hookFailures")}
              </span>
            ),
            children: <KoboHookFailuresTab />,
          },
          {
            key: "3",
            label: (
              <span>
                <DatabaseOutlined /> {t("monitoring.koboForms")}
              </span>
            ),
            children: <KoboFormsTab />,
          },
        ]}
      />
    </MonitoringWrapper>
  );
};

export default MonitoringPage;
